"use client";

import { useState, type FC } from "react";
import {
  DownloadIcon,
  EyeIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  Loader2Icon,
  PresentationIcon
} from "lucide-react";
import {
  makeAssistantToolUI,
  useAuiState,
  type ToolCallMessagePartComponent
} from "@assistant-ui/react";
import { useAuthHeader } from "react-auth-kit";
import { useArtifactLinkMutation, type AgentFileLink } from "@/agents-v2/api";
import { useAgentsChat } from "@/agents-v2/components/common/chat-provider";
import { EXPORT_ARTIFACT_TOOL_NAME } from "@/agents-v2/components/common/export-artifact-tool-name";
import { openDownloadUrl } from "@/agents-v2/uploads/uploaded-files";
import {
  asRecord,
  getStringValue,
  parseMaybeJson
} from "@/agents-v2/utils/unknown";
import { Button } from "@/agents-v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/agents-v2/components/ui/dialog";

export { EXPORT_ARTIFACT_TOOL_NAME } from "@/agents-v2/components/common/export-artifact-tool-name";

export type GeneratedDownload = {
  id: string;
  filename: string;
  displayName: string;
};

type FileKind = "pdf" | "word" | "excel" | "powerpoint" | "unsupported";

const WORD_EXTENSIONS = new Set(["doc", "docx"]);
const EXCEL_EXTENSIONS = new Set(["xls", "xlsx"]);
const POWERPOINT_EXTENSIONS = new Set(["ppt", "pptx"]);

const filenameFromPath = (path: string): string => {
  const segments = path.split(/[\\/]/).filter(Boolean);
  return segments[segments.length - 1] || "download";
};

const getFileExtension = (filename: string): string => {
  const [, extension = ""] =
    /(?:\.([^.]+))?$/.exec(filename.toLowerCase()) ?? [];
  return extension;
};

const getFileKind = (filename: string): FileKind => {
  const extension = getFileExtension(filename);
  if (extension === "pdf") return "pdf";
  if (WORD_EXTENSIONS.has(extension)) return "word";
  if (EXCEL_EXTENSIONS.has(extension)) return "excel";
  if (POWERPOINT_EXTENSIONS.has(extension)) return "powerpoint";
  return "unsupported";
};

const canPreviewFile = (filename: string): boolean =>
  getFileKind(filename) !== "unsupported";

const getPreviewUrl = (
  download: GeneratedDownload,
  artifactUrl: string
): string => {
  if (getFileKind(download.filename) === "pdf") {
    return `${artifactUrl}#toolbar=1&navpanes=0&view=FitH`;
  }

  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    artifactUrl
  )}`;
};

const FileKindIcon: FC<{ filename: string; className?: string }> = ({
  filename,
  className
}) => {
  const kind = getFileKind(filename);
  if (kind === "excel") return <FileSpreadsheetIcon className={className} />;
  if (kind === "powerpoint") return <PresentationIcon className={className} />;
  return <FileTextIcon className={className} />;
};

function DownloadRow({
  download,
  threadId,
  token,
  onPreview
}: {
  download: GeneratedDownload;
  threadId: string | null;
  token: string | null;
  onPreview: (
    download: GeneratedDownload,
    artifact: AgentFileLink
  ) => Promise<void>;
}) {
  const previewLinkMutation = useArtifactLinkMutation({
    token,
    action: "preview"
  });
  const downloadLinkMutation = useArtifactLinkMutation({
    token,
    action: "download"
  });
  const isPreviewing = previewLinkMutation.isPending;
  const isDownloading = downloadLinkMutation.isPending;
  const isBusy = isDownloading || isPreviewing;

  const handlePreview = async () => {
    if (!threadId || !token || !canPreviewFile(download.filename)) return;
    try {
      const artifact = await previewLinkMutation.mutateAsync({
        threadId,
        filename: download.filename
      });
      await onPreview(download, artifact);
    } catch (error) {
      console.error("Failed to preview generated artifact", error);
    } finally {
      previewLinkMutation.reset();
    }
  };

  const handleDownload = async () => {
    if (!threadId || !token) return;
    try {
      const artifact = await downloadLinkMutation.mutateAsync({
        threadId,
        filename: download.filename
      });
      openDownloadUrl(artifact.url, artifact.filename ?? download.displayName);
    } catch (error) {
      console.error("Failed to download generated artifact", error);
    } finally {
      downloadLinkMutation.reset();
    }
  };

  return (
    <div
      data-slot="generated-download-row"
      className="flex items-center gap-2 px-4 py-3 text-sm"
    >
      <FileKindIcon
        filename={download.filename}
        className="size-4 shrink-0 text-muted-foreground"
      />
      <span className="min-w-0 flex-1 truncate font-medium">
        {download.displayName}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        {canPreviewFile(download.filename) ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2.5 text-sm"
            disabled={!threadId || !token || isBusy}
            aria-busy={isPreviewing}
            onClick={() => void handlePreview()}
          >
            {isPreviewing ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              <EyeIcon className="size-4" />
            )}
            Preview
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          disabled={!threadId || !token || isBusy}
          aria-busy={isDownloading}
          onClick={() => void handleDownload()}
        >
          {isDownloading ? (
            <Loader2Icon className="size-4 animate-spin" />
          ) : (
            <DownloadIcon className="size-4" />
          )}
          Download
        </Button>
      </div>
    </div>
  );
}

export function GeneratedDownloads({
  downloads
}: {
  downloads: readonly GeneratedDownload[];
}) {
  const getAuthHeader = useAuthHeader();
  const rawHeader = getAuthHeader();
  const token = typeof rawHeader === "string" && rawHeader ? rawHeader.replace(/^Bearer\s+/i, "") : null;
  const { currentThreadId } = useAgentsChat();
  const [previewFile, setPreviewFile] = useState<GeneratedDownload | null>(
    null
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handlePreview = async (
    download: GeneratedDownload,
    artifact: AgentFileLink
  ) => {
    if (!currentThreadId || !token || !canPreviewFile(download.filename))
      return;
    setPreviewUrl(artifact.url);
    setPreviewFile(download);
  };

  if (downloads.length === 0) return null;

  return (
    <>
      <div
        data-slot="generated-downloads"
        className="aui-generated-downloads mt-2 overflow-hidden rounded-lg border bg-card"
      >
        <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
          <DownloadIcon className="size-4 text-muted-foreground" />
          <span className="font-medium text-foreground text-sm">
            {downloads.length} download{downloads.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="divide-y">
          {downloads.map((download) => (
            <DownloadRow
              key={download.id}
              download={download}
              threadId={currentThreadId}
              token={token}
              onPreview={handlePreview}
            />
          ))}
        </div>
      </div>

      <Dialog
        open={previewFile !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewFile(null);
            setPreviewUrl(null);
          }
        }}
      >
        <DialogContent className="grid h-[82vh] max-w-[90vw] grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden p-0">
          <div className="flex min-w-0 items-center gap-2 border-b px-4 py-3">
            {previewFile ? (
              <FileKindIcon
                filename={previewFile.filename}
                className="size-4 shrink-0 text-muted-foreground"
              />
            ) : null}
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">
                {previewFile?.displayName ?? "Preview"}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Generated file preview
              </DialogDescription>
            </div>
          </div>
          {previewFile && previewUrl ? (
            <iframe
              className="size-full min-h-0 border-0 bg-white"
              src={getPreviewUrl(previewFile, previewUrl)}
              title={`Preview ${previewFile.displayName}`}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

const ExportArtifactToolRender: ToolCallMessagePartComponent<
  unknown,
  unknown
> = ({ result, status, toolCallId }) => {
  const shouldShowDownloads = useAuiState(
    (state) => !state.thread.isRunning || !state.message.isLast
  );

  if (status?.type !== "complete" || !shouldShowDownloads) return null;

  const downloads = extractArtifactFilenames(result).map((filename, index) => ({
    id: `${toolCallId}:${filename}:${index}`,
    filename,
    displayName: filenameFromPath(filename)
  }));

  return <GeneratedDownloads downloads={downloads} />;
};

export const ExportArtifactToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: EXPORT_ARTIFACT_TOOL_NAME,
  render: ExportArtifactToolRender
});

const extractArtifactFilenames = (result: unknown): string[] => {
  const parsed = parseMaybeJson(result);
  const candidates = Array.isArray(parsed)
    ? parsed
    : [
        asRecord(parsed).artifacts,
        asRecord(parsed).files,
        asRecord(parsed).filenames,
        asRecord(parsed).filename,
        asRecord(parsed).path
      ];

  return candidates
    .flatMap((candidate) => {
      if (Array.isArray(candidate)) return candidate;
      return candidate == null ? [] : [candidate];
    })
    .map((candidate) => {
      if (typeof candidate === "string") return candidate.trim();
      const record = asRecord(candidate);
      return (
        getStringValue(record, "filename") ??
        getStringValue(record, "name") ??
        getStringValue(record, "path")
      );
    })
    .filter((filename): filename is string => Boolean(filename));
};
